import React from 'react';

import { Row, Col } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { Dropdown } from 'components/Forms';
import {
  formatPatientConditions,
  getConditionValue,
  getPreference,
  getRPMReason
} from 'utils/Helpers/Patient';
import { getMedicalConditionOptions, diabetesKeys } from 'constants/constants';
import { getNewCondition } from 'utils/Conditions';

interface IConditionsProps {
  handlePatientUpdate: (updatedPatient: any, conditions: any) => void;
  patient?: IPatient;
}

const Conditions = ({ handlePatientUpdate, patient }: IConditionsProps) => {
  const intl = useIntl();

  /* Patient condition */
  const patientConditions =
    patient?.conditions.filter((condition) => condition.condition !== '') || [];

  const formattedPatientConditions: ISelectOption[] = formatPatientConditions(
    patientConditions,
    intl
  );

  /* Primary and secondary RPM reason */
  const primaryCondition: ICondition = getRPMReason(
    patient?.primaryRpmReason || '',
    patientConditions
  );

  const secondaryCondition: ICondition = getRPMReason(
    patient?.secondaryRpmReason || '',
    patientConditions
  );

  const otherConditions = patientConditions.filter(
    (condition) =>
      condition.condition !== primaryCondition.condition &&
      condition.condition !== secondaryCondition.condition
  );

  const formattedPrimaryCondition: ISelectOption[] = formatPatientConditions(
    [primaryCondition],
    intl
  );

  const formattedSecondaryCondition: ISelectOption[] = formatPatientConditions(
    [secondaryCondition],
    intl
  );

  /* Dropdown options */
  const allConditions: ISelectOption[] = getMedicalConditionOptions(
    intl,
    patient?.genderId ? patient.genderId : '2'
  );

  const otherConditionOptions: ISelectOption[] = allConditions.filter(
    (option) =>
      getPreference(option.value) !==
        getPreference(
          formattedPrimaryCondition[0]?.value
        ) /* Remove primary from others option */ &&
      getPreference(option.value) !==
        getPreference(
          formattedSecondaryCondition[0]?.value
        ) /* Remove secondary from others option */
  );

  const handleRPMReasonChange = (e: ISelectOption, isPrimary: boolean) => {
    if (e == null) {
      handleRemoveRPMReason(isPrimary);
    } else {
      handleAddRPMReason(e, isPrimary);
    }
  };

  const handleAddRPMReason = (e: ISelectOption, isPrimary: boolean) => {
    const data: any = {};
    const updatedPatient: any = {};
    data['conditions'] = [];

    /* Condition/reason to add */
    const conditionKey = e.value;
    const preference = isPrimary ? 'primaryRpmReason' : 'secondaryRpmReason';
    const preferenceValue = getPreference(e.value);
    const updatedConditions = [...patientConditions];

    const switchSecondaryToPrimary =
      formattedSecondaryCondition[0] !== undefined &&
      formattedSecondaryCondition[0].value === conditionKey;
    const switchSecondaryDiabetesToPrimaryDiabetes =
      diabetesKeys.includes(conditionKey) &&
      isPrimary &&
      diabetesKeys.includes(formattedSecondaryCondition[0]?.value);
    const removeDiabetesFromOthers =
      otherConditions.some((el) => diabetesKeys.includes(el.condition)) &&
      diabetesKeys.includes(conditionKey);

    let updatePrimary = isPrimary;
    let updateSecondary = !isPrimary;
    let index = 0;

    if (isPrimary && switchSecondaryToPrimary) {
      const conditionToRemove = getNewCondition(formattedPrimaryCondition[0].value, true);

      /* Update data for API */
      data['secondaryRpmReason'] = null; // Delete secondary reason
      data['primaryRpmReason'] = preferenceValue; // Add new primary reason
      data['conditions'].push(conditionToRemove);

      /* Update data for store */
      updatePrimary = true;
      updateSecondary = true;
      index = updatedConditions.findIndex(
        (condition) => condition.condition === conditionToRemove.condition
      );

      updatedConditions.splice(index, 1);
    } else if (switchSecondaryDiabetesToPrimaryDiabetes) {
      /* If updating primary to diabetes and second is already diabetes */
      const conditionToAdd = getNewCondition(conditionKey, false);
      const conditionToRemove = getNewCondition(formattedSecondaryCondition[0].value, true);

      /* Only update conditions for API and store  */
      data['conditions'].push(conditionToAdd);
      data['conditions'].push(conditionToRemove);
      data['primaryRpmReason'] = preferenceValue; // Add new primary reason
      data['secondaryRpmReason'] = null; // Delete secondary reason

      updatedConditions.push(conditionToAdd);
      index = updatedConditions.findIndex(
        (condition) => condition.condition === conditionToRemove.condition
      );

      updatedConditions.splice(index, 1);
      updatePrimary = true;
      updateSecondary = true;
    } else if (removeDiabetesFromOthers) {
      /* Remove old diabetes value from store */
      index = updatedConditions.findIndex((condition) => condition.diabetesType);
      updatedConditions.splice(index, 1);

      /* Add new diabetes type */
      const conditionToAdd = getNewCondition(conditionKey, false);
      data['conditions'].push(conditionToAdd);
      updatedConditions.push(conditionToAdd);

      /* Add new RPM reason */
      data[preference] = preferenceValue;

      /* remove previous condition */
      const previousRPMReason = isPrimary
        ? formattedPrimaryCondition[0]
        : formattedSecondaryCondition[0];
      if (previousRPMReason) {
        const conditionToRemove = getNewCondition(previousRPMReason.value, true);
        data['conditions'].push(conditionToRemove);
        index = updatedConditions.findIndex(
          (condition) => condition.condition === previousRPMReason.value
        );
        updatedConditions.splice(index, 1);
      }
    } else {
      /* Check if condition already in patient's conditions */
      const conditionFound = formattedPatientConditions.find((c) => c.value === conditionKey);
      const previousRPMReason = isPrimary
        ? formattedPrimaryCondition[0]
        : formattedSecondaryCondition[0];

      const switchDiabetesType =
        diabetesKeys.includes(conditionKey) && diabetesKeys.includes(previousRPMReason?.value);

      /* Set new reason data */
      data[preference] = preferenceValue;

      /* RPM reason is a new condition, add to patient's conditions */
      if (!conditionFound) {
        const conditionToAdd = getNewCondition(conditionKey, false);

        /* Update data for API and store  */
        data['conditions'].push(conditionToAdd);
        updatedConditions.push(conditionToAdd);
      }

      /* Delete previous RPM reason */
      if (previousRPMReason) {
        const conditionToRemove = getNewCondition(previousRPMReason.value, true);

        /* Update data for API and store  */
        if (!switchDiabetesType) data['conditions'].push(conditionToRemove); // Delete previous reason from conditions unless switching diabetes
        index = updatedConditions.findIndex(
          (condition) => condition.condition === previousRPMReason.value
        );
        updatedConditions.splice(index, 1);
      }
    }

    /* Update patient */
    updatedPatient['conditions'] = updatedConditions;
    if (updatePrimary) updatedPatient['primaryRpmReason'] = data['primaryRpmReason'];
    if (updateSecondary) updatedPatient['secondaryRpmReason'] = data['secondaryRpmReason'];
    handlePatientUpdate(updatedPatient, data['conditions']);
  };

  const handleRemoveRPMReason = (isPrimary: boolean) => {
    const data: any = {};
    const updatedPatient: any = {};
    const preference = isPrimary ? 'primaryRpmReason' : 'secondaryRpmReason';

    /* Switch secondary to primary */
    if (isPrimary && formattedSecondaryCondition[0] !== undefined) {
      const value = getPreference(formattedSecondaryCondition[0].value);
      data[preference] = value; // Set value of primary RPM reason to previous secondary RPM reason
      data['secondaryRpmReason'] = null; // Remove secondary reason

      /* Remove previous primary reason */
      const conditionToRemove = getNewCondition(formattedPrimaryCondition[0].value, true);

      /* Update data for API and store */
      data['conditions'] = [conditionToRemove];
      const updatedConditions = patientConditions.filter(
        (cond) => cond.condition !== conditionToRemove.condition
      );

      /* Update patient */
      updatedPatient[preference] = value;
      updatedPatient['secondaryRpmReason'] = null;
      updatedPatient['conditions'] = updatedConditions;
    } else {
      /* Remove reason and associated condition */
      data[preference] = null;
      const conditionToRemoveKey = isPrimary
        ? formattedPrimaryCondition[0].value
        : formattedSecondaryCondition[0].value;
      const conditionToRemove = getNewCondition(conditionToRemoveKey, true);

      /* Update data for API and store */
      data['conditions'] = [conditionToRemove];
      const updatedConditions = patientConditions.filter(
        (cond) => cond.condition !== conditionToRemoveKey
      );

      /* Update patient */
      updatedPatient[preference] = null;
      updatedPatient['conditions'] = updatedConditions;
    }
    handlePatientUpdate(updatedPatient, data['conditions']);
  };

  const handleAddCondition = (e: ISelectOption[]) => {
    e.map((condition) => {
      const updatedPatient: any = {};
      let data: any = {};
      let updatedConditions = [];
      const conditionKey = condition.value;
      const conditionFound = !formattedPatientConditions.find((c) => c.value === conditionKey);
      if (conditionFound) {
        const newCondition = getNewCondition(conditionKey, false);
        data = {
          conditions: [newCondition]
        };

        const propertyName = getPreference(conditionKey);
        if (propertyName === 'diabetesType') {
          updatedConditions = patientConditions.filter((condition) => !condition.diabetesType);
          if (
            primaryCondition.condition !== 'diabetesType' &&
            secondaryCondition.condition !== 'diabetesType'
          ) {
            updatedConditions.push(newCondition);
          }
        } else {
          updatedConditions = [...patientConditions];
          updatedConditions.push(newCondition);
        }
        updatedPatient['conditions'] = updatedConditions;
        handlePatientUpdate(updatedPatient, data['conditions']);
      }
    });
  };

  const handleRemoveCondition = (e: ISelectOption[]) => {
    const updatedPatient: any = {};
    let data: any = {};
    let removeCondition: ICondition = { condition: '' };

    /* Filter to prevent removing primary/secondary */
    let otherConditions = [...patientConditions];
    otherConditions = otherConditions.filter(
      (cond) => cond.condition !== primaryCondition?.condition
    );
    otherConditions = otherConditions.filter(
      (cond) => cond.condition !== secondaryCondition?.condition
    );

    /* If last one to be removed... */
    if (e == null) {
      otherConditions.map((condition) => {
        if (condition.condition === otherConditions[0].condition) {
          /* Remove it (update value to null) */
          removeCondition = getNewCondition(condition.condition, true);
        }
      });
      data = {
        conditions: [removeCondition]
      };
      const updatedConditions: ICondition[] = [];
      if (primaryCondition && primaryCondition.condition) {
        updatedConditions.push({
          [getPreference(primaryCondition.condition)]: getConditionValue(
            primaryCondition.condition,
            intl
          ),
          condition: primaryCondition.condition
        });
      }
      if (secondaryCondition && secondaryCondition.condition) {
        updatedConditions.push({
          [getPreference(secondaryCondition.condition)]: getConditionValue(
            secondaryCondition.condition,
            intl
          ),
          condition: secondaryCondition.condition
        });
      }

      updatedPatient['conditions'] = updatedConditions;
    } else {
      let index = 0;
      otherConditions.map((condition, i) => {
        /* If not in selection, remove it (update value to null) */
        if (!e.find((c) => c.value === condition.condition)) {
          index = i;
          removeCondition = getNewCondition(condition.condition, true);
        }
      });
      data = {
        conditions: [removeCondition]
      };

      const updatedConditions = [...otherConditions];
      updatedConditions.splice(index, 1);
      if (primaryCondition && primaryCondition.condition) {
        updatedConditions.push({
          [getPreference(primaryCondition.condition)]: getConditionValue(
            primaryCondition.condition,
            intl
          ),
          condition: primaryCondition.condition
        });
      }
      if (secondaryCondition && secondaryCondition.condition) {
        updatedConditions.push({
          [getPreference(secondaryCondition.condition)]: getConditionValue(
            secondaryCondition.condition,
            intl
          ),
          condition: secondaryCondition.condition
        });
      }
      updatedPatient['conditions'] = updatedConditions;
    }
    handlePatientUpdate(updatedPatient, data['conditions']);
  };

  const handleConditionChange = (e: ISelectOption[]) => {
    let length = patient?.primaryRpmReason
      ? patientConditions.length - 1
      : patientConditions.length;
    length = patient?.secondaryRpmReason ? length - 1 : length;
    if (e === null || e.length < length) {
      handleRemoveCondition(e);
    } else {
      handleAddCondition(e);
    }
  };

  return (
    <>
      <Row className="pb-3">
        <Col md={6}>
          <Dropdown
            variant="form"
            label={intl.formatMessage({ id: 'primaryConditionKey' })}
            options={allConditions.filter(
              (condition) => condition.value !== formattedPrimaryCondition[0]?.value
            )}
            onChange={(data: ISelectOption) => handleRPMReasonChange(data, true)}
            isClearable={true}
            isSearchable={true}
            value={formattedPrimaryCondition}
            minWidth={250}
            wrapLines={true}
            fitContent
            dataTestId="condition-primary-dropdown"
          />
        </Col>
        <Col md={6}>
          <Dropdown
            variant="form"
            label={intl.formatMessage({ id: 'secondaryConditionKey' })}
            options={otherConditionOptions}
            onChange={(data: ISelectOption) => handleRPMReasonChange(data, false)}
            isDisabled={primaryCondition.condition === '' || primaryCondition == null}
            isClearable={true}
            isSearchable={true}
            value={formattedSecondaryCondition}
            minWidth={250}
            wrapLines={true}
            fitContent
            dataTestId="condition-secondary-dropdown"
          />
        </Col>
      </Row>
      <Row className="pb-3">
        <Col md={12}>
          <Dropdown
            variant="form"
            label={intl.formatMessage({ id: 'otherConditionsKey' })}
            options={otherConditionOptions}
            onChange={(data: ISelectOption[]) => handleConditionChange(data)}
            isClearable={false}
            isMulti
            isSearchable={true}
            value={formattedPatientConditions.filter(
              (option) =>
                option.value !== formattedPrimaryCondition[0]?.value &&
                option.value !== formattedSecondaryCondition[0]?.value
            )}
            minWidth={250}
            wrapLines={true}
            fitContent
            dataTestId="condition-other-dropdown"
          />
        </Col>
      </Row>
    </>
  );
};

export default Conditions;
